
import {
  defineComponent, ref, computed, inject,
} from 'vue';
import { useClipboard } from '@vueuse/core';
import { useRoute } from 'vue-router';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useMainStore } from '@/store/mainStore';
import DriveService from '@/modules/drive/services/DriveService';

export default defineComponent({
  setup() {
    const { copy, copied } = useClipboard();
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const route = useRoute();
    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localize = inject('$localize') as Function;

    const isPublic = computed(() => (!!driveStore.currentCase.publicAccessed));
    const isNetwork = computed(() => route.meta.isNetwork);

    const caseUrl = computed(() => (process.env.VUE_APP_PUBLIC_DOMAIN_URL || '') + driveStore.currentCase.externalUrl);

    const shareData = ref({
      title: $localize('drive.public.case.shared.title'),
      text: $localize('drive.public.case.shared.text'),
      url: caseUrl,
    });

    async function shareLink() {
      if (mainStore.isNative) {
        try {
          await navigator.share(shareData.value);
        } catch (error) {
          console.error('Error sharing:', error);
        }
      }
    }

    function onCopy() {
      copy(caseUrl.value);
    }

    const urlAccessUpdating = ref(false);

    function changeAccessByUrl() {
      urlAccessUpdating.value = true;
      const payload = {
        caseId: route.params.id as string,
        flag: !driveStore.currentCase.accessedByExternalUrl,
      };
      return DriveService.handleCaseExternalUrl(payload).then((r) => {
        if (r.success) {
          driveStore.currentCase.accessedByExternalUrl = r.results.data as boolean;
          driveStore.$patch((state) => {
            state.currentCase.accessedByExternalUrl = r.results.data as boolean;
            state.cases.data = [state.currentCase, ...state.cases.data.filter((cas) => cas.id !== state.currentCase.id)];
          });
        }
        urlAccessUpdating.value = false;
        return false;
      });
    }

    const publicAccessUpdating = ref(false);

    async function makeCaseExternalAccessed() {
      publicAccessUpdating.value = true;
      const currentCaseCopy = JSON.parse(JSON.stringify(driveStore.currentCase));
      if (!driveStore.currentCase.publicAccessed) {
        driveStore.makeCasePublic(route.params.id as string)
          .then((response) => {
            if (response.success) {
              driveStore.$patch((state) => {
                state.currentCase.publicAccessed = true;
                currentCaseCopy.publicAccessed = true;
                state.publicCases.data = [currentCaseCopy, ...state.publicCases.data.filter((cas) => cas.id !== currentCaseCopy.id)];
                state.cases.data = [currentCaseCopy, ...state.cases.data.filter((cas) => cas.id !== currentCaseCopy.id)];
              });
            }
            publicAccessUpdating.value = false;
          });
      }
      if (driveStore.currentCase.publicAccessed) {
        driveStore.unmakeCasePublic(route.params.id as string)
          .then((response) => {
            if (response.success) {
              driveStore.$patch((state) => {
                state.currentCase.publicAccessed = false;
                currentCaseCopy.publicAccessed = false;
                state.publicCases.data = [...state.publicCases.data.filter((cas) => cas.id !== currentCaseCopy.id)];
                state.cases.data = [currentCaseCopy, ...state.cases.data.filter((cas) => cas.id !== currentCaseCopy.id)];
              });
            }
            publicAccessUpdating.value = false;
          });
      }
      return false;
    }

    return {
      urlAccessUpdating,
      changeAccessByUrl,

      publicAccessUpdating,
      makeCaseExternalAccessed,
      //
      shareLink,
      onCopy,
      copied,
      driveStore,
      isPublic,
      isNetwork,
      caseUrl,
      mainStore,
    };
  },
});
